import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  McCard,
  McButton,
  McDialog,
  McInput,
  McSelect,
  McIcon,
  McNotification,
  McLoadingIndicator,
  McBadge,
  McTabBar,
  McTab,
} from '@maersk-global/mds-react-wrapper';
import {
  refDataService,
  parseColumnDefs,
  parseRowData,
  RefSchema,
  RefRow,
} from '../services/metadataService';
import { useAuth } from '../context/AuthContext';
import { roleConfig } from '../config/roles';
import { TerminalSelect } from '../components/Common/TerminalSelect';
import { useApplicationAccess } from '../hooks/useApplicationAccess';
import InlineDataGrid from '../components/refdata/InlineDataGrid';
import ColumnBuilder from '../components/refdata/ColumnBuilder';
import { useNotification } from '../context/NotificationContext';

function normalizeTableKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, '_');
}

function describeSchemaLoadError(error: unknown, terminal: string, app: string): string {
  const err = error as any;
  const status = err?.response?.status as number | undefined;
  const serverMessage = err?.response?.data?.message as string | undefined;

  if (status === 401) return 'Session expired. Please sign in again and retry.';
  if (status === 403) return 'You do not have permission to view reference tables for this app.';
  if (status === 404) return `No schema endpoint found for ${terminal}/${app}. Check terminal/app values.`;
  if (status === 429) return 'Too many requests. Please wait a moment and retry.';
  if (status && status >= 500) return 'Server error while loading schemas. Retry or contact support.';
  if (serverMessage) return serverMessage;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 'No network connection detected.';
  return 'Could not fetch schemas.';
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  page: {
    padding: '0',
    width: '100%',
    fontFamily: 'Barlow, sans-serif',
    backgroundColor: '#f5f7fa',
    height: 'calc(100vh - 96px)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.6rem',
  } as React.CSSProperties,

  connectionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.25rem 0.6rem',
    backgroundColor: '#ffffff',
    border: '1px solid #d9e0e8',
    borderRadius: '8px',
    flexWrap: 'wrap' as const,
    flexShrink: 0,
    fontSize: '0.78rem',
  } as React.CSSProperties,

  layout: {
    display: 'flex',
    gap: '0.6rem',
    alignItems: 'stretch',
    flex: 1,
    minHeight: 0,
  } as React.CSSProperties,

  sidebar: {
    width: '224px',
    flexShrink: 0,
    backgroundColor: '#ffffff',
    border: '1px solid #d9e0e8',
    borderRadius: '8px',
    padding: '0.5rem',
    color: '#1a2332',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column' as const,
  } as React.CSSProperties,

  main: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
  } as React.CSSProperties,

  workbench: {
    border: '1px solid #d9e0e8',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#1a2332',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    minHeight: 0,
  } as React.CSSProperties,

  sqlPreview: {
    fontFamily: 'Menlo, Monaco, Consolas, monospace',
    fontSize: '0.78rem',
    color: '#1b5e20',
    backgroundColor: '#f1f8e9',
    border: '1px solid #d7e8c4',
    borderRadius: '4px',
    padding: '0.4rem 0.6rem',
    whiteSpace: 'pre-wrap' as const,
    overflowX: 'auto' as const,
  } as React.CSSProperties,

  dataCanvas: {
    padding: '0.5rem 0.9rem 0.9rem',
    backgroundColor: '#ffffff',
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  } as React.CSSProperties,

  topTabs: {
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    padding: '0 0.75rem',
    flexShrink: 0,
  } as React.CSSProperties,

  dataToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.6rem',
    marginBottom: '0.45rem',
    flexWrap: 'wrap' as const,
    padding: '0.1rem 0',
  } as React.CSSProperties,

  dataToolbarPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flex: 1,
    minWidth: '260px',
  } as React.CSSProperties,

  dataToolbarSearch: {
    flex: 1,
    minWidth: '220px',
  } as React.CSSProperties,

  dataToolbarMeta: {
    fontSize: '0.7rem',
    color: '#64748b',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  dataToolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginLeft: 'auto',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  dataToolbarGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    paddingLeft: '0.35rem',
    borderLeft: '1px solid #e2e8f0',
  } as React.CSSProperties,

  dataToolbarLabel: {
    fontSize: '0.68rem',
    color: '#475569',
    whiteSpace: 'nowrap' as const,
    letterSpacing: '0.01em',
  } as React.CSSProperties,

  dataToolbarExtensions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
  } as React.CSSProperties,

  schemaItem: (active: boolean): React.CSSProperties => ({
    padding: '0.4rem 0.55rem',
    borderRadius: '5px',
    cursor: 'pointer',
    marginBottom: '0.15rem',
    backgroundColor: active ? '#e8f3f5' : 'transparent',
    border: active ? '1px solid #0a6e82' : '1px solid transparent',
    transition: 'background 0.15s',
  }),

  historyItem: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'flex-start',
    padding: '0.6rem 0',
    borderBottom: '1px solid var(--mds-color-neutral-08)',
  } as React.CSSProperties,

  label: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--mds-color-neutral-70)',
    display: 'block',
    marginBottom: '0.3rem',
  } as React.CSSProperties,
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function RefDataViewerPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const { hasRole } = useAuth();
  const canManage = hasRole(roleConfig.manageRefData);

  // ─── Connection bar state ─────────────────────────────────────────────────
  const [terminalInput, setTerminalInput] = useState('');
  const [appInput, setAppInput] = useState('');
  const [terminal, setTerminal] = useState('');
  const { accessibleApplications } = useApplicationAccess();
  const [app, setApp] = useState('');

  // ─── Selection state ──────────────────────────────────────────────────────
  const [selectedSchema, setSelectedSchema] = useState<RefSchema | null>(null);
  // Rows-per-request selector. Defaults to 10 so the first view is small and fast;
  // the user can request larger pages. The backend caps a single page at 500.
  const ROW_PAGE_SIZES = [10, 25, 50, 100];
  const [pageSize, setPageSize] = useState(10);
  // Free-text filter (matched server-side across the whole row payload), debounced.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sqlPreviewOpen, setSqlPreviewOpen] = useState(false);
  const [refTableRows, setRefTableRows] = useState<Record<string, RefRow[]>>({});

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput.trim()), 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // ─── Queries ──────────────────────────────────────────────────────────────
  const schemasQuery = useQuery({
    queryKey: ['ref-schemas', terminal, app],
    queryFn: () => refDataService.listSchemas(terminal, app),
    enabled: !!terminal && !!app,
    retry: (failureCount, error: any) => {
      const status = error?.response?.status;
      if (status === 401 || status === 403 || status === 404) return false;
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * (2 ** attemptIndex), 8000),
  });

  const rowsQuery = useInfiniteQuery({
    queryKey: ['ref-rows', terminal, app, selectedSchema?.tableKey, pageSize, searchTerm],
    queryFn: ({ pageParam }) =>
      refDataService.getRowsPage(terminal, app, selectedSchema!.tableKey, {
        cursor: pageParam as string | number,
        limit: pageSize,
        q: searchTerm || undefined,
      }),
    initialPageParam: 0 as string | number,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore && lastPage.nextCursor != null ? lastPage.nextCursor : undefined,
    enabled: !!selectedSchema && !!terminal && !!app,
  });

  // ─── Derived values ───────────────────────────────────────────────────────
  const cols = useMemo(
    () => (selectedSchema ? parseColumnDefs(selectedSchema) : []),
    [selectedSchema]
  );

  // Accumulated rows across all loaded pages.
  const allRows = useMemo(
    () => rowsQuery.data?.pages.flatMap(p => p.items) ?? [],
    [rowsQuery.data]
  );

  const mayHaveMore = rowsQuery.hasNextPage;

  const sqlPreviewQuery = useMemo(() => {
    if (!selectedSchema) return '';

    const sqlEscape = (value: string) => value.replace(/'/g, "''");
    const schemaId = selectedSchema.schemaId ? sqlEscape(selectedSchema.schemaId) : null;
    const escapedTerminal = sqlEscape(terminal);
    const escapedApp = sqlEscape(app);
    const escapedTableKey = sqlEscape(selectedSchema.tableKey);
    const escapedSearch = sqlEscape(searchTerm.toLowerCase());

    const whereLines = [
      '  r.is_active = TRUE',
      schemaId
        ? `  AND r.schema_id = '${schemaId}'::uuid`
        : `  AND r.terminal_id = '${escapedTerminal}'`,
      schemaId
        ? ''
        : `  AND r.app_id = '${escapedApp}'`,
      schemaId
        ? ''
        : `  AND r.table_key = '${escapedTableKey}'`,
      searchTerm ? `  AND lower(r.data::text) LIKE '%${escapedSearch}%'` : '',
    ].filter(Boolean);

    return [
      'SELECT',
      '  r.row_id,',
      '  r.data,',
      '  r.created_at,',
      '  r.updated_at,',
      '  r.created_by,',
      '  r.updated_by',
      'FROM ref_data_rows r',
      'WHERE',
      ...whereLines,
      'ORDER BY r.id DESC',
      `LIMIT ${pageSize};`,
    ].join('\n');
  }, [selectedSchema, terminal, app, searchTerm, pageSize]);

  // Infinite-scroll sentinel: fetch the next page when it scrolls into view.
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = rowsQuery;
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, allRows.length]);

  // Pre-fetch ref options so foreign-key columns can resolve display labels.
  // Depends on terminal + app so that changing the loaded dataset resets lookups correctly.

  // ─── Pre-fill from URL params (e.g. navigated back from DeveloperDocsPage) ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('terminal');
    const a = params.get('app');
    if (t) setTerminalInput(t.toUpperCase());
    if (a) setAppInput(a);
    if (t && a) {
      const cleanT = t.trim().toUpperCase();
      const cleanA = a.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      setTerminal(cleanT);
      setApp(cleanA);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cols.length || !terminal || !app) return;
    const refTables = Array.from(
      new Set(
        cols
          .filter(c => c.type === 'ref' && c.refTable)
          .map(c => c.refTable as string),
      ),
    );
    refTables.forEach(tableKey => {
      if (refTableRows[tableKey]) return;
      refDataService
        .getRows(terminal, app, tableKey, 0, 500)
        .then(rows => {
          setRefTableRows(prev => ({ ...prev, [tableKey]: rows }));
        })
        .catch(() => {/* ignore */});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols, terminal, app]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  function getRefOptions(col: any): { value: string; label: string }[] {
    const rows = refTableRows[col.refTable as string] ?? [];
    return rows
      .map(row => {
        try {
          const d = parseRowData(row);
          const value = String(d[col.refValueKey as string] ?? '');
          const label = String(d[col.refDisplayKey as string] ?? value);
          return { value, label };
        } catch { return { value: '', label: '' }; }
      })
      .filter(o => o.value !== '');
  }

  function resolveRefValue(col: any, rawValue: string): string {
    const rows = refTableRows[col.refTable as string];
    if (!rows) return rawValue;
    const found = rows.find(r => {
      try { return String(parseRowData(r)[col.refValueKey as string]) === rawValue; }
      catch { return false; }
    });
    if (!found) return `${rawValue} ⚠`;
    try {
      return String(parseRowData(found)[col.refDisplayKey as string] ?? rawValue);
    } catch { return rawValue; }
  }

  function handleLoad() {
    const t = terminalInput.trim().toUpperCase();
    // Sanitize app: lowercase, spaces → underscore, strip non-alphanumeric/underscore
    const a = appInput.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (!t || !a) return;
    if (!/^[A-Z0-9_-]{2,20}$/.test(t)) {
      showNotification('Terminal format is invalid. Use letters, numbers, dash, or underscore.', 'error');
      return;
    }
    setTerminal(t);
    setApp(a);
    setSelectedSchema(null);
    setSearchInput('');
    setSearchTerm('');
    setRefTableRows({});
    queryClient.invalidateQueries({ queryKey: ['ref-schemas', t, a] });
  }

  const [activeTab, setActiveTab] = useState<'data' | 'api' | 'get-data' | 'schema' | 'history'>('data');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [auditRowId, setAuditRowId] = useState<string | null>(null);

  const rowHistoryQuery = useInfiniteQuery({
    queryKey: ['ref-row-history', terminal, app, selectedSchema?.tableKey, auditRowId],
    queryFn: ({ pageParam }) =>
      refDataService.getRowHistory(terminal, app, selectedSchema!.tableKey, auditRowId!, {
        beforeVersion: pageParam as number | undefined,
        limit: 20,
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.length < 20 ? undefined : lastPage[lastPage.length - 1]?.schemaVersion,
    enabled: !!auditRowId && !!terminal && !!app && !!selectedSchema,
  });

  const historyEntries = useMemo(
    () => rowHistoryQuery.data?.pages.flat() ?? [],
    [rowHistoryQuery.data]
  );

  // Open a row's audit trail in the dedicated History tab (replaces the old inline panel).
  const openHistory = useCallback((rowId: string) => {
    setAuditRowId(rowId);
    setActiveTab('history');
  }, []);
  // Schema editor state (Phase 3)
  const [schemaEditorJson, setSchemaEditorJson] = useState('');
  const [schemaChangeSummary, setSchemaChangeSummary] = useState('');

  // Create schema dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTableKey, setNewTableKey] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [createMode, setCreateMode] = useState<'manual' | 'csv'>('manual');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const normalizedNewTableKey = useMemo(() => normalizeTableKey(newTableKey), [newTableKey]);
  const isCreateTableKeyValid = useMemo(
    () => /^[a-zA-Z0-9_]{1,63}$/.test(normalizedNewTableKey),
    [normalizedNewTableKey],
  );

  function handleSelectSchema(schema: RefSchema) {
    setSelectedSchema(schema);
    setActiveTab('data');
    setAuditRowId(null);
    setSearchInput('');
    setSearchTerm('');
    setSchemaEditorJson(
      Array.isArray(schema.columnDefs)
        ? JSON.stringify(schema.columnDefs, null, 2)
        : (schema.columnDefs as string) ?? '[]'
    );
    setSchemaChangeSummary('');
  }

  function endpointUrl() {
    return `/api/v1/${terminal}/${app}/schemas/${selectedSchema?.tableKey ?? ''}/rows`;
  }

  function handleCopyUrl() {
    navigator.clipboard.writeText(window.location.origin + endpointUrl()).then(() => {
      showNotification('Endpoint URL copied.', 'success');
    }).catch(() => {
      showNotification('Failed to copy URL.', 'error');
    });
  }

  function handleCopyJson() {
    navigator.clipboard.writeText(JSON.stringify(allRows, null, 2)).then(() => {
      showNotification('JSON response copied.', 'success');
    }).catch(() => {
      showNotification('Failed to copy JSON.', 'error');
    });
  }

  function handleCopySqlPreview() {
    if (!sqlPreviewQuery) return;
    navigator.clipboard.writeText(sqlPreviewQuery).then(() => {
      showNotification('SQL copied.', 'success');
    }).catch(() => {
      showNotification('Failed to copy SQL.', 'error');
    });
  }

  function handleCreateQueryFromSql() {
    if (!sqlPreviewQuery) return;
    navigate('/queries/create', {
      state: {
        prefillSql: sqlPreviewQuery,
        prefillTerminal: terminal,
        prefillAppId: app,
        source: 'ref-data',
        tableKey: selectedSchema?.tableKey ?? '',
      },
    });
  }

  function handleDownloadRows() {
    if (!selectedSchema || allRows.length === 0) return;

    const baseName = `${terminal}_${app}_${selectedSchema.tableKey}_${Date.now()}`;
    let content = '';
    let mime = '';
    let extension = '';

    if (exportFormat === 'json') {
      content = JSON.stringify(allRows.map(r => parseRowData(r)), null, 2);
      mime = 'application/json;charset=utf-8';
      extension = 'json';
    } else {
      const escapeCsv = (value: unknown) => {
        if (value == null) return '';
        const str = typeof value === 'string' ? value : (typeof value === 'object' ? JSON.stringify(value) : String(value));
        if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
        return str;
      };

      const headers = cols.map((c: any) => c.label || c.key);
      const lines = allRows.map((row) => {
        const data = parseRowData(row);
        return cols.map((c: any) => escapeCsv(data[c.key])).join(',');
      });
      content = [headers.map(escapeCsv).join(','), ...lines].join('\n');
      mime = 'text/csv;charset=utf-8';
      extension = 'csv';
    }

    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${baseName}.${extension}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);

    showNotification(`Downloaded ${allRows.length} row${allRows.length !== 1 ? 's' : ''} as ${exportFormat.toUpperCase()}.`, 'success');
  }

  // ─── Write mutations (visible only when canManage) ────────────────────────
  const createRowMutation = useMutation({
    mutationFn: ({ data }: { data: Record<string, unknown> }) =>
      refDataService.createRow(terminal, app, selectedSchema!.tableKey, {
        data,
      }),
    onSuccess: (newRow) => {
      // Refetch the row pages so the new row appears in cursor order.
      void newRow;
      queryClient.invalidateQueries({ queryKey: ['ref-rows', terminal, app, selectedSchema?.tableKey] });
      showNotification('Row created.', 'success');
    },
    onError: (error: any) => {
      const detail: string = error?.response?.data?.detail ?? '';
      const fieldErrors: any[] = error?.response?.data?.fieldErrors ?? [];
      const fieldMsg = fieldErrors
        .map((f: any) => `${f.field ?? f.fieldName ?? 'field'}: ${f.message ?? f.defaultMessage ?? ''}`)
        .join('; ');
      showNotification(fieldMsg || detail || 'Failed to create row.', 'error');
    },
  });

  const updateRowMutation = useMutation({
    mutationFn: ({ rowId, data }: { rowId: string; data: Record<string, unknown>; sortOrder?: number }) =>
      refDataService.updateRow(terminal, app, selectedSchema!.tableKey, rowId, {
        data,
      }),
    onSuccess: (updatedRow) => {
      void updatedRow;
      queryClient.invalidateQueries({ queryKey: ['ref-rows', terminal, app, selectedSchema?.tableKey] });
      showNotification('Row updated.', 'success');
    },
    onError: (error: any) => {
      const detail: string = error?.response?.data?.detail ?? '';
      const fieldErrors: any[] = error?.response?.data?.fieldErrors ?? [];
      const fieldMsg = fieldErrors
        .map((f: any) => `${f.field ?? f.fieldName ?? 'field'}: ${f.message ?? f.defaultMessage ?? ''}`)
        .join('; ');
      showNotification(fieldMsg || detail || 'Failed to update row.', 'error');
    },
  });

  const deleteRowMutation = useMutation({
    mutationFn: (rowId: string) =>
      refDataService.deleteRow(terminal, app, selectedSchema!.tableKey, rowId),
    onSuccess: (_, rowId) => {
      void rowId;
      queryClient.invalidateQueries({ queryKey: ['ref-rows', terminal, app, selectedSchema?.tableKey] });
      setPendingDeleteId(null);
      showNotification('Row deleted.', 'success');
    },
    onError: () => showNotification('Failed to delete row.', 'error'),
  });

  const bulkMutation = useMutation({
    mutationFn: (jsonStr: string) => {
      const parsed = JSON.parse(jsonStr);
      const rows: Array<Record<string, unknown>> = Array.isArray(parsed)
        ? parsed.map((row: unknown, idx: number) => {
            if (row !== null && typeof row === 'object' && !Array.isArray(row)) {
              return row as Record<string, unknown>;
            }
            if (typeof row === 'string') {
              const maybeObj = JSON.parse(row);
              if (maybeObj !== null && typeof maybeObj === 'object' && !Array.isArray(maybeObj)) {
                return maybeObj as Record<string, unknown>;
              }
            }
            throw new Error(`Invalid bulk row at index ${idx}. Each row must be a JSON object.`);
          })
        : [];
      if (rows.length === 0) {
        throw new Error('Bulk payload must contain at least one row object.');
      }

      return refDataService.bulkReplace(terminal, app, selectedSchema!.tableKey, { rows });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ref-rows', terminal, app, selectedSchema?.tableKey] });
      showNotification('Bulk import complete.', 'success');
    },
    onError: (error: any) => showNotification(error?.message || 'Bulk import failed. Check JSON format.', 'error'),
  });

  const updateSchemaMutation = useMutation({
    mutationFn: () => {
      const parsedColumnDefs = JSON.parse(schemaEditorJson || '[]');
      if (!Array.isArray(parsedColumnDefs)) {
        throw new Error('Schema JSON must be an array of column definitions.');
      }
      return refDataService.updateSchema(terminal, app, selectedSchema!.tableKey, {
        displayName: selectedSchema!.displayName ?? selectedSchema!.tableKey,
        description: selectedSchema!.description ?? undefined,
        columnDefs: parsedColumnDefs as Array<Record<string, unknown>>,
        changeSummary: schemaChangeSummary || 'Schema updated via UI',
      });
    },
    onSuccess: (updated) => {
      setSelectedSchema(updated);
      queryClient.invalidateQueries({ queryKey: ['ref-schemas', terminal, app] });
      showNotification('Schema saved successfully.', 'success');
    },
    onError: (error: any) => showNotification(error?.message || 'Failed to save schema.', 'error'),
  });

  const createSchemaMutation = useMutation({
    mutationFn: () =>
      refDataService.createSchema(terminal, app, {
        tableKey: normalizedNewTableKey,
        displayName: newDisplayName.trim() || undefined,
        description: newDescription.trim() || undefined,
        columnDefs: [],
        uiHints: {},
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['ref-schemas', terminal, app] });
      setShowCreateDialog(false);
      setNewTableKey('');
      setNewDisplayName('');
      setNewDescription('');
      handleSelectSchema(created);
      showNotification(`Table "${created.tableKey}" created.`, 'success');
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      if (status === 409) {
        showNotification(`Table key "${normalizedNewTableKey}" already exists for ${terminal}/${app}.`, 'error');
        return;
      }
      showNotification(error?.response?.data?.message || 'Failed to create table.', 'error');
    },
  });

  const uploadCsvMutation = useMutation({
    mutationFn: () => {
      if (!selectedFile) {
        throw new Error('No file selected');
      }
      return refDataService.uploadCsv(
        terminal,
        app,
        selectedFile,
        newTableKey.trim() || undefined,
        newDisplayName.trim() || undefined,
        newDescription.trim() || undefined,
        false
      );
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['ref-schemas', terminal, app] });
      setShowCreateDialog(false);
      setNewTableKey('');
      setNewDisplayName('');
      setNewDescription('');
      setCreateMode('manual');
      setSelectedFile(null);
      handleSelectSchema(created);
      showNotification(`Table "${created.tableKey}" created and CSV data imported successfully.`, 'success');
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      if (status === 409) {
        showNotification(`Table key "${normalizedNewTableKey}" already exists for ${terminal}/${app}.`, 'error');
        return;
      }
      showNotification(error?.response?.data?.message || error?.message || 'Failed to upload CSV.', 'error');
    },
  });

  function handleDeleteRequest(rowId: string) {
    setPendingDeleteId(rowId);
  }

  function handleDeleteConfirm() {
    if (pendingDeleteId !== null) {
      deleteRowMutation.mutate(pendingDeleteId);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      {/* ── Connection bar ─────────────────────────────────────────────────── */}
      <div style={s.connectionBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: 'auto' }}>
          {canManage && (
            <span style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--mds-color-brand-primary)' }}>
              ✓ Edit access enabled
            </span>
          )}
        </div>
        <div>
          <TerminalSelect
            value={terminalInput}
            onChange={(v: string) => setTerminalInput(v)}
            label=""
            placeholder="Select terminal…"
            style={{ minWidth: '200px' }}
          />
        </div>
        <div>
          <McSelect
            fit="small"
            label=""
            placeholder="Select application…"
            value={appInput}
            data={accessibleApplications.map(a => ({
              label: a.name || a.applicationId,
              value: (a.name || a.applicationId).toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
            }))}
            optionselected={(e: any) => {
              const val = e.detail?.value || e.detail || e.target?.value || '';
              setAppInput(val);
            }}
            listsearch={true}
            style={{ minWidth: '160px' }}
          />
        </div>
        <McButton fit="small" onClick={handleLoad} disabled={!terminalInput || !appInput || schemasQuery.isFetching}>
          <McIcon icon={schemasQuery.isFetching ? 'refresh' : 'search'} slot="prefix" />
          {schemasQuery.isFetching ? 'Loading…' : 'Load'}
        </McButton>
      </div>

      {/* ── Body ──────────────────────────────────────────────────────────── */}
      {terminal && app && (
        <div style={s.layout}>
          {/* ── Sidebar ─────────────────────────────────────────────────── */}
          <div style={sidebarCollapsed ? { ...s.sidebar, width: '40px', minWidth: '40px', overflow: 'hidden' } : s.sidebar}>
            {/* Collapse / expand toggle */}
            <div style={{ display: 'flex', justifyContent: sidebarCollapsed ? 'center' : 'flex-end', marginBottom: '0.35rem' }}>
              <McButton
                appearance="neutral"
                fit="small"
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                onClick={() => setSidebarCollapsed(v => !v)}
              >
                <McIcon icon={sidebarCollapsed ? 'chevron-right' : 'chevron-left'} />
              </McButton>
            </div>
            {!sidebarCollapsed && (
            <>
            <div style={{ fontWeight: 700, fontSize: '0.72rem', color: '#475569', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.45rem', padding: '0 0.25rem' }}>
              Navigator
            </div>
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.55rem', padding: '0 0.25rem' }}>
              terminal/{terminal || '-'} app/{app || '-'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.45rem', padding: '0 0.25rem' }}>
              <span style={{ fontWeight: 700, fontSize: '0.72rem', color: '#475569', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Tables</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                {terminal && app && (
                  <McButton
                    fit="small"
                    appearance="neutral"
                    title="Refresh schema list"
                    onClick={() => queryClient.invalidateQueries({ queryKey: ['ref-schemas', terminal, app] })}
                  >
                    <McIcon icon="refresh" slot="prefix" />
                    Refresh
                  </McButton>
                )}
                {canManage && terminal && app && (
                  <McButton fit="small" appearance="neutral" title="Create new reference table" onClick={() => setShowCreateDialog(true)}>
                    <McIcon icon="plus" slot="prefix" />
                    New
                  </McButton>
                )}
              </div>
            </div>

            {schemasQuery.isLoading && <McLoadingIndicator fit="small" />}
            {schemasQuery.isFetching && !schemasQuery.isLoading && (
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.45rem', padding: '0 0.25rem' }}>
                Updating schema list…
              </div>
            )}
            {schemasQuery.isError && (
              <McNotification
                appearance="error"
                heading="Load error"
                body={describeSchemaLoadError(schemasQuery.error, terminal, app)}
              />
            )}
            {schemasQuery.data?.length === 0 && (
              <div style={{ textAlign: 'center', padding: '1.25rem 0.5rem' }}>
                <McIcon icon="table" style={{ fontSize: '1.5rem', color: '#94a3b8', marginBottom: '0.5rem' }} />
                <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.75rem' }}>
                  No tables yet for<br /><strong>{terminal}/{app}</strong>
                </p>
                {canManage && (
                  <McButton fit="small" onClick={() => setShowCreateDialog(true)}>
                    <McIcon icon="plus" slot="prefix" />
                    Create first table
                  </McButton>
                )}
                {!canManage && (
                  <p style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '0.65rem' }}>
                    Ask an editor/admin to create the first table.
                  </p>
                )}
              </div>
            )}

            {schemasQuery.data?.map(schema => (
              <div
                key={schema.tableKey}
                style={s.schemaItem(selectedSchema?.tableKey === schema.tableKey)}
                onClick={() => handleSelectSchema(schema)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.4rem' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {schema.displayName || schema.tableKey}
                  </span>
                  <span style={{ flexShrink: 0 }}>
                    <McBadge label={`v${schema.schemaVersion}`} display="inline" />
                    {!((schema.isActive ?? schema.active) ?? true) && (
                      <McBadge
                        label="inactive"
                        display="inline"
                        style={{ marginLeft: '0.3rem', '--mc-badge-background-color': 'var(--mds-color-neutral-30)' } as any}
                      />
                    )}
                  </span>
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.05rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {schema.tableKey}
                </div>
              </div>
            ))}
            </>
            )}
          </div>

          {/* ── Main content ─────────────────────────────────────────────── */}
          <div style={s.main}>
            {!selectedSchema ? (
              <McCard style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--mds-color-neutral-60)' }}>
                  <McIcon icon="table" style={{ fontSize: '2rem', marginBottom: '0.75rem', display: 'block' }} />
                  {schemasQuery.data?.length === 0 ? (
                    <>
                      <p style={{ marginBottom: '0.4rem', fontWeight: 600 }}>No reference tables yet</p>
                      <p style={{ fontSize: '0.85rem', marginBottom: '1.25rem' }}>
                        <strong>{terminal}/{app}</strong> has no registered reference tables.
                        {canManage ? ' Create one to get started.' : ' Contact an admin to create tables.'}
                      </p>
                      {canManage && (
                        <McButton onClick={() => setShowCreateDialog(true)}>
                          <McIcon icon="plus" slot="prefix" />
                          Create first table
                        </McButton>
                      )}
                    </>
                  ) : (
                    <p>Select a table from the sidebar to view its data.</p>
                  )}
                </div>
              </McCard>
            ) : (
              <div style={s.workbench}>
                {/* Compact header — table identity + actions */}
                <div style={{
                  padding: '0.55rem 0.85rem',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '0.75rem',
                  backgroundColor: '#ffffff',
                  color: '#1a2332',
                  flexShrink: 0,
                  flexWrap: 'wrap',
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: '1.02rem' }}>
                        {selectedSchema.displayName || selectedSchema.tableKey}
                      </span>
                      <McBadge label={`schema v${selectedSchema.schemaVersion}`} display="inline" />
                      <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                        {terminal} / {app} / {selectedSchema.tableKey}
                      </span>
                    </div>
                    {selectedSchema.description && (
                      <p style={{ fontSize: '0.78rem', color: '#475569', marginTop: '0.15rem', marginBottom: 0 }}>
                        {selectedSchema.description}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <McButton
                      fit="small"
                      variant="plain"
                      appearance="neutral"
                      title="Copy generated SQL"
                      onClick={handleCopySqlPreview}
                    >
                      <McIcon icon="copy" slot="prefix" />
                      Copy SQL
                    </McButton>
                    <McButton
                      fit="small"
                      variant="outlined"
                      appearance="neutral"
                      title="Open Create Query with this SQL"
                      onClick={handleCreateQueryFromSql}
                    >
                      <McIcon icon="plus" slot="prefix" />
                      Create Query
                    </McButton>
                    <McButton
                      fit="small"
                      variant="plain"
                      appearance="neutral"
                      title={sqlPreviewOpen ? 'Hide SQL preview' : 'Show SQL preview'}
                      onClick={() => setSqlPreviewOpen(v => !v)}
                    >
                      <McIcon icon="code" slot="prefix" />
                      {sqlPreviewOpen ? 'Hide SQL' : 'SQL'}
                    </McButton>
                  </div>
                </div>

                {/* Collapsible SQL preview chip */}
                {sqlPreviewOpen && (
                  <div style={{ padding: '0.5rem 0.85rem', borderBottom: '1px solid #e2e8f0', backgroundColor: '#fbfdf9', flexShrink: 0 }}>
                    <div style={s.sqlPreview}>
                      {sqlPreviewQuery}
                    </div>
                  </div>
                )}

                {/* Tabs bar */}
                <div style={s.topTabs}>
                  <McTabBar
                    currentindex={
                      activeTab === 'data' ? 0
                        : activeTab === 'api' ? 1
                        : activeTab === 'get-data' ? 2
                        : activeTab === 'schema' ? 3
                        : 4
                    }
                    tabchange={(e: any) => {
                      const index = e.detail;
                      if (index === 1) {
                        const params = new URLSearchParams({
                          tab: 'refdata',
                          terminal,
                          app,
                          tableKey: selectedSchema?.tableKey ?? '',
                        });
                        window.location.href = `/developer-docs?${params.toString()}`;
                        return;
                      }
                      setActiveTab(
                        index === 0 ? 'data'
                          : index === 2 ? 'get-data'
                          : index === 3 ? 'schema'
                          : 'history',
                      );
                    }}
                    style={{ '--mc-tab-bar-background': 'transparent' } as any}
                  >
                    <McTab slot="tab" label="Data" icon="table" />
                    <McTab slot="tab" label="API" icon="file" />
                    <McTab slot="tab" label="Get Data" icon="download" />
                    <McTab slot="tab" label="Schema" icon="settings" />
                    <McTab slot="tab" label="History" icon="clock" />
                  </McTabBar>
                </div>

                <div style={s.dataCanvas}>
                {/* ── Tab: Data ─────────────────────────────────────────────── */}
                {activeTab === 'data' && (
                  <div style={{ padding: '0.8rem 0 0' }}>
                    {rowsQuery.isError && (
                      <McNotification appearance="error" heading="Load error" body="Failed to load rows." />
                    )}
                    {pendingDeleteId !== null && (
                      <McDialog
                        open
                        heading="Confirm Delete"
                        closed={() => setPendingDeleteId(null)}
                      >
                        <p>Delete row #{pendingDeleteId}? This cannot be undone.</p>
                        <McButton
                          slot="primaryAction"
                          appearance="error"
                          variant="filled"
                          onClick={handleDeleteConfirm}
                          disabled={deleteRowMutation.isPending}
                        >
                          Delete
                        </McButton>
                        <McButton slot="secondaryAction" variant="outlined" onClick={() => setPendingDeleteId(null)}>
                          Cancel
                        </McButton>
                      </McDialog>
                    )}
                    {/* Enterprise toolbar (extension-ready for future controls/actions) */}
                    <div style={s.dataToolbar}>
                      <div style={s.dataToolbarPrimary}>
                        <div style={s.dataToolbarSearch}>
                          <McInput
                            fit="small"
                            label=""
                            placeholder="Filter rows — type to search any field…"
                            value={searchInput}
                            input={(e: any) => setSearchInput(e.detail?.value ?? e.target?.value ?? '')}
                          />
                        </div>
                        <span style={s.dataToolbarMeta}>
                          {allRows.length} row{allRows.length !== 1 ? 's' : ''} loaded
                        </span>
                      </div>

                      <div style={s.dataToolbarActions}>
                        <div style={s.dataToolbarGroup}>
                          <span style={s.dataToolbarLabel}>Rows</span>
                          <McSelect
                            fit="small"
                            label=""
                            value={String(pageSize)}
                            data={ROW_PAGE_SIZES.map(n => ({ label: String(n), value: String(n) }))}
                            optionselected={(e: any) => {
                              const val = e.detail?.value ?? e.detail ?? '';
                              const next = parseInt(val, 10);
                              if (!Number.isNaN(next)) setPageSize(next);
                            }}
                            style={{ width: '74px' }}
                          />
                        </div>

                        {allRows.length > 0 && (
                          <div style={s.dataToolbarGroup}>
                            <McSelect
                              fit="small"
                              label=""
                              value={exportFormat}
                              data={[
                                { label: 'CSV', value: 'csv' },
                                { label: 'JSON', value: 'json' },
                              ]}
                              optionselected={(e: any) => {
                                const next = e.detail?.value ?? e.detail ?? e.target?.value ?? 'csv';
                                setExportFormat(next === 'json' ? 'json' : 'csv');
                              }}
                              style={{ width: '86px' }}
                            />
                            <McButton variant="outlined" fit="small" onClick={handleDownloadRows}>
                              <McIcon icon="download" slot="prefix" />
                              Download
                            </McButton>
                          </div>
                        )}

                        <div style={s.dataToolbarExtensions}>
                          {/* Reserved for future quick actions (column presets, saved filters, bulk ops). */}
                        </div>
                      </div>
                    </div>
                    <InlineDataGrid
                      cols={cols}
                      rows={allRows}
                      canManage={canManage}
                      isLoading={rowsQuery.isLoading}
                      isFetching={rowsQuery.isFetching}
                      mayHaveMore={!!mayHaveMore}
                      totalCount={allRows.length}
                      getRefOptions={getRefOptions}
                      resolveRefValue={resolveRefValue}
                      onSaveNew={async (data) => {
                        await createRowMutation.mutateAsync({ data });
                      }}
                      onSaveEdit={async (rowId, data, sortOrder) => {
                        await updateRowMutation.mutateAsync({ rowId, data, sortOrder });
                      }}
                      onDeleteRequest={handleDeleteRequest}
                      onBulkReplace={(json) => bulkMutation.mutate(json)}
                      isBulkPending={bulkMutation.isPending}
                      onLoadMore={() => fetchNextPage()}
                      onViewHistory={openHistory}
                    />

                    {/* Infinite-scroll sentinel + status */}
                    {mayHaveMore && (
                      <div ref={loadMoreRef} style={{ display: 'flex', justifyContent: 'center', padding: '0.75rem', minHeight: '1px' }}>
                        {isFetchingNextPage && <McLoadingIndicator fit="small" label="Loading more rows…" />}
                      </div>
                    )}
                    {searchTerm && (
                      <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#64748b', margin: '0.4rem 0' }}>
                        Showing {allRows.length} matching row{allRows.length !== 1 ? 's' : ''} for &ldquo;{searchTerm}&rdquo;
                        {mayHaveMore ? ' (scroll for more)' : ''}
                      </p>
                    )}
                  </div>
                )}

                {/* ── Tab: API — navigates away, never rendered ──────────── */}

                {/* ── Tab: History ─────────────────────────────────────────── */}
                {activeTab === 'history' && (
                  <div style={{ padding: '0.8rem 0 0' }}>
                    {!auditRowId && (
                      <div style={{ padding: '1.5rem', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
                        <McIcon icon="clock" style={{ fontSize: '1.5rem', display: 'block', margin: '0 auto 0.5rem' }} />
                        Select a row&rsquo;s <strong>history</strong> action on the Data tab to view its audit trail here.
                      </div>
                    )}
                    {auditRowId && (
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.8rem', backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1a2332', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <McIcon icon="clock" style={{ fontSize: '1rem' }} />
                            Audit History —
                            <code style={{ fontSize: '0.75rem', color: '#475569', fontFamily: 'monospace' }}>{auditRowId.slice(0, 8)}…</code>
                          </span>
                          <McButton fit="small" variant="plain" appearance="neutral" title="Back to data" onClick={() => { setAuditRowId(null); setActiveTab('data'); }}>
                            <McIcon icon="times" />
                          </McButton>
                        </div>
                        {rowHistoryQuery.isLoading && <div style={{ padding: '1rem' }}><McLoadingIndicator fit="small" /></div>}
                        {rowHistoryQuery.isError && (
                          <McNotification appearance="error" heading="Failed to load audit history" body="Please try again." />
                        )}
                        {!rowHistoryQuery.isLoading && historyEntries.length === 0 && (
                          <p style={{ padding: '0.75rem', color: '#64748b', fontSize: '0.85rem', margin: 0 }}>No audit records found for this row.</p>
                        )}
                        {historyEntries.length > 0 && (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                  {['Version', 'Change', 'By', 'At', 'Summary', 'Data snapshot'].map(h => (
                                    <th key={h} style={{ padding: '0.45rem 0.75rem', color: '#475569', fontWeight: 600, textAlign: 'left', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {historyEntries.map((entry, i) => (
                                  <tr key={i} style={{ borderTop: '1px solid #f1f5f9', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ padding: '0.45rem 0.75rem', color: '#1a2332', fontWeight: 600 }}>v{entry.schemaVersion}</td>
                                    <td style={{ padding: '0.45rem 0.75rem' }}>
                                      <McBadge label={entry.changeType || '—'} display="inline" />
                                    </td>
                                    <td style={{ padding: '0.45rem 0.75rem', color: '#374151', whiteSpace: 'nowrap' }}>{entry.changedBy}</td>
                                    <td style={{ padding: '0.45rem 0.75rem', color: '#374151', whiteSpace: 'nowrap' }}>
                                      {entry.changedAt ? new Date(entry.changedAt).toLocaleString() : '—'}
                                    </td>
                                    <td style={{ padding: '0.45rem 0.75rem', color: '#374151' }}>{entry.changeSummary || '—'}</td>
                                    <td style={{ padding: '0.45rem 0.75rem' }}>
                                      {entry.payload != null && (
                                        <details>
                                          <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: '#0a6e82' }}>View</summary>
                                          <pre style={{ margin: '0.35rem 0 0', fontSize: '0.72rem', color: '#1e293b', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '0.4rem', maxWidth: '300px', overflowX: 'auto' }}>
                                            {JSON.stringify(entry.payload, null, 2)}
                                          </pre>
                                        </details>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {rowHistoryQuery.hasNextPage && (
                          <div style={{ display: 'flex', justifyContent: 'center', padding: '0.6rem' }}>
                            <McButton
                              variant="outlined"
                              fit="small"
                              disabled={rowHistoryQuery.isFetchingNextPage}
                              onClick={() => rowHistoryQuery.fetchNextPage()}
                            >
                              {rowHistoryQuery.isFetchingNextPage ? 'Loading…' : 'Load older versions'}
                            </McButton>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Tab: Get Data ─────────────────────────────────────────── */}
                {activeTab === 'get-data' && (
                  <div style={{ padding: '0.8rem 0 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                      <span style={{
                        fontFamily: 'monospace',
                        fontSize: '0.82rem',
                        backgroundColor: '#f3f4f6',
                        color: '#111827',
                        padding: '0.3rem 0.6rem',
                        borderRadius: '4px',
                        flex: 1,
                        minWidth: 0,
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                      }}>
                        <strong style={{ color: '#0a6e82' }}>GET</strong>
                        {' '}{endpointUrl()}
                      </span>
                      <McButton variant="outlined" fit="small" onClick={handleCopyUrl}>
                        <McIcon icon="copy" slot="prefix" />
                        Copy URL
                      </McButton>
                      <McButton variant="outlined" fit="small" onClick={handleCopyJson} disabled={allRows.length === 0}>
                        <McIcon icon="copy" slot="prefix" />
                        Copy JSON
                      </McButton>
                    </div>
                    {allRows.length === 0 ? (
                      <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>
                        No rows loaded yet — switch to the Data tab first.
                      </p>
                    ) : (
                      <>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.4rem' }}>
                          Response ({allRows.length} row{allRows.length !== 1 ? 's' : ''}{mayHaveMore ? ', first page only' : ''}):
                        </p>
                        <pre style={{
                          margin: 0,
                          padding: '0.75rem',
                          borderRadius: '5px',
                          backgroundColor: '#f8fafc',
                          color: '#1e293b',
                          border: '1px solid #e2e8f0',
                          fontSize: '0.76rem',
                          fontFamily: 'monospace',
                          overflowX: 'auto',
                          maxHeight: '500px',
                          overflowY: 'auto',
                        }}>
                          {JSON.stringify(allRows, null, 2)}
                        </pre>
                      </>
                    )}
                  </div>
                )}

                {/* ── Tab: Schema ────────────────────────────────────────────── */}
                {activeTab === 'schema' && (
                  <div style={{ padding: '0.8rem 0 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1a2332' }}>
                        Column definitions — {selectedSchema.displayName || selectedSchema.tableKey}
                      </span>
                      {!canManage && (
                        <span style={{ fontSize: '0.78rem', color: 'var(--mds-color-neutral-50)' }}>
                          (read-only — edit access required)
                        </span>
                      )}
                    </div>
                    <ColumnBuilder
                      value={schemaEditorJson}
                      onChange={setSchemaEditorJson}
                      disabled={!canManage}
                    />
                    {canManage && (
                      <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <McInput
                          hiddenlabel
                          label="Change summary"
                          placeholder="Change summary (optional)"
                          value={schemaChangeSummary}
                          input={(e: any) => setSchemaChangeSummary(e.detail?.value ?? e.target?.value ?? '')}
                          style={{ flex: 1, minWidth: '200px' }}
                        />
                        <McButton
                          fit="small"
                          onClick={() => updateSchemaMutation.mutate()}
                          disabled={updateSchemaMutation.isPending}
                        >
                          <McIcon icon="save" slot="prefix" />
                          {updateSchemaMutation.isPending ? 'Saving…' : 'Save Schema'}
                        </McButton>
                      </div>
                    )}
                  </div>
                )}

                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Create schema dialog ───────────────────────────────────────────── */}
      {showCreateDialog && (
        <McDialog
          open
          heading="Create Reference Table"
          closed={() => { 
            setShowCreateDialog(false); 
            setNewTableKey(''); 
            setNewDisplayName(''); 
            setNewDescription(''); 
            setCreateMode('manual');
            setSelectedFile(null);
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', minWidth: '340px', padding: '0.25rem 0 0.5rem' }}>
            <McTabBar
              currentindex={createMode === 'manual' ? 0 : 1}
              tabchange={(e: any) => {
                setCreateMode(e.detail === 0 ? 'manual' : 'csv');
              }}
              style={{ marginBottom: '0.5rem', '--mc-tab-bar-background': 'transparent' } as any}
            >
              <McTab slot="tab" label="Create empty table" />
              <McTab slot="tab" label="Import from CSV" />
            </McTabBar>

            {createMode === 'csv' && (
              <div>
                <label style={s.label}>CSV File <span style={{ color: 'var(--mds-color-feedback-danger)' }}>*</span></label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
                  <input
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setSelectedFile(file);
                      if (file && !newTableKey.trim()) {
                        const filename = file.name;
                        const dotIdx = filename.lastIndexOf('.');
                        const nameWithoutExt = dotIdx > 0 ? filename.substring(0, dotIdx) : filename;
                        const cleanKey = nameWithoutExt.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_');
                        setNewTableKey(cleanKey);
                      }
                    }}
                  />
                  <McButton
                    fit="small"
                    appearance="neutral"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <McIcon icon="upload" slot="prefix" />
                    Choose File
                  </McButton>
                  <span style={{ fontSize: '0.8rem', color: selectedFile ? 'var(--mds-color-neutral-100)' : 'var(--mds-color-neutral-60)' }}>
                    {selectedFile ? selectedFile.name : 'No file selected'}
                  </span>
                </div>
              </div>
            )}

            <div>
              <label style={s.label}>
                Table Key {createMode === 'manual' && <span style={{ color: 'var(--mds-color-feedback-danger)' }}>*</span>}
              </label>
              <McInput
                hiddenlabel
                label="Table Key"
                value={newTableKey}
                placeholder={createMode === 'csv' ? "Optional (auto-derived from file name if blank)" : "e.g. vessel_types"}
                input={(e: any) => setNewTableKey(e.detail?.value ?? e.target?.value ?? '')}
              />
              <span style={{ fontSize: '0.72rem', color: 'var(--mds-color-neutral-60)', marginTop: '0.2rem', display: 'block' }}>
                Lowercase letters, numbers and underscores. Used as the unique table identifier.
              </span>
              {newTableKey.trim() && !isCreateTableKeyValid && (
                <span style={{ fontSize: '0.72rem', color: 'var(--mds-color-feedback-danger)', marginTop: '0.25rem', display: 'block' }}>
                  Invalid key. Use letters, numbers, and underscores only (max 63 chars).
                </span>
              )}
              {isCreateTableKeyValid && normalizedNewTableKey !== newTableKey.trim() && (
                <span style={{ fontSize: '0.72rem', color: 'var(--mds-color-neutral-60)', marginTop: '0.25rem', display: 'block' }}>
                  Will be created as: {normalizedNewTableKey}
                </span>
              )}
            </div>
            <div>
              <label style={s.label}>Display Name</label>
              <McInput
                hiddenlabel
                label="Display Name"
                value={newDisplayName}
                placeholder="e.g. Vessel Types"
                input={(e: any) => setNewDisplayName(e.detail?.value ?? e.target?.value ?? '')}
              />
            </div>
            <div>
              <label style={s.label}>Description</label>
              <McInput
                hiddenlabel
                label="Description"
                value={newDescription}
                placeholder="Optional description of this table…"
                input={(e: any) => setNewDescription(e.detail?.value ?? e.target?.value ?? '')}
              />
            </div>
          </div>
          {createMode === 'manual' ? (
            <McButton
              slot="primaryAction"
              disabled={!newTableKey.trim() || !isCreateTableKeyValid || createSchemaMutation.isPending}
              onClick={() => createSchemaMutation.mutate()}
            >
              {createSchemaMutation.isPending ? 'Creating…' : 'Create Table'}
            </McButton>
          ) : (
            <McButton
              slot="primaryAction"
              disabled={!selectedFile || (!!newTableKey.trim() && !isCreateTableKeyValid) || uploadCsvMutation.isPending}
              onClick={() => uploadCsvMutation.mutate()}
            >
              {uploadCsvMutation.isPending ? 'Importing…' : 'Import Table'}
            </McButton>
          )}
          <McButton
            slot="secondaryAction"
            variant="outlined"
            onClick={() => { 
              setShowCreateDialog(false); 
              setNewTableKey(''); 
              setNewDisplayName(''); 
              setNewDescription(''); 
              setCreateMode('manual');
              setSelectedFile(null);
            }}
          >
            Cancel
          </McButton>
        </McDialog>
      )}
    </div>
  );
}
